import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Switch from 'react-switch'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import GatewayPicker from 'components/gatewayPicker'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import { getCashboxGroupPay } from 'services/cashbox'
import { postGatewayPayment } from 'services/easyPay'
import { Card, Notice, PrimaryButton, apiMessage } from 'modules/cashbox/ui'
import { useCashbox } from 'modules/cashbox/hooks'
import { goToGateway } from 'modules/gatewayHandoff'
import useAvailableGateways from 'utilities/hooks/useAvailableGateways'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'
import useGetInitPaymentOs from 'pages/wallet/hooks/useGetInitPaymentOs'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'

/**
 * پرداخت گروهی — the manager paying other members' shares in one go.
 *
 * Goes through the v2 gateway payment with one `msp` item per member. The item
 * carries the MEMBERSHIP id and the backend works out the receiver and the
 * current cycle from it, which retires the membership→member join this screen
 * used to do by hand (getting that join wrong credits a stranger's share).
 *
 * It is the same call the other pay screens make, so the wallet comes with it:
 * the debit is shares + commission, a covered payment names no gateway, and a
 * partial one charges the bank only the difference.
 */
export default function CashboxGroupPayPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { cashbox, isLoading: boxLoading } = useCashbox(id)

    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-group-pay', id],
        () => getCashboxGroupPay(id!),
        { enabled: Boolean(id) }
    )

    const [selected, setSelected] = useState<Set<number>>(new Set())
    const [submitting, setSubmitting] = useState(false)
    const handoff = useGatewayHandoff()

    // `init-group-pay` lists gateways by id only, and the v2 payment names its
    // gateway by `unique_code`. Ask which ones will take THIS cashbox instead —
    // the same rule the payment endpoint enforces, so whatever is offered here
    // is accepted there. A cashbox is `cbx` on the allowlist.
    const { gateways, selectedCode, setSelectedCode, hasNoGateway } =
        useAvailableGateways(
            useMemo(
                () => (id ? [{ type: 'cbx' as const, id: Number(id) }] : []),
                [id]
            ),
            { enabled: Boolean(id) }
        )

    const rows = data?.memberships ?? []
    const selectedRows = rows.filter((row) => selected.has(row.id))
    const total = selectedRows.reduce((sum, row) => sum + row.remaining_amount, 0)
    const commission = selectedRows.reduce((sum, row) => sum + row.commission, 0)
    const due = total + commission

    // The wallet. The debit is shares PLUS commission, so the cover check is
    // against `due`: checking `total` alone hands the backend more than the
    // balance holds.
    const profile = JSON.parse(localStorage.getItem('profile') ?? 'null')
    const { data: walletData } = useGetInitPaymentOs(profile?.id)
    const balance = Number(walletData?.wallet_balance ?? 0)
    const [useWallet, setUseWallet] = useState(false)
    const walletCoversAll = useWallet && due > 0 && balance >= due
    const payable = useWallet ? Math.max(0, due - balance) : due
    // Covered in full ⇒ no bank, so nothing to pick. With no gateway at all a
    // PARTIAL cover leaves the rest with nowhere to go, so the switch is only
    // offered when it can settle everything.
    const needsGateway = !walletCoversAll
    const hasWallet = balance > 0 && due > 0 && (!hasNoGateway || balance >= due)

    const pay = async () => {
        if (handoff.isLocked()) return
        if (needsGateway && !selectedCode) {
            toast.error('روش پرداخت را انتخاب کنید')
            return
        }
        // Before the request, not after it: an attempt that fails still has to
        // leave a trace.
        posthog.capture('cashbox_group_pay_started', {
            cashbox_id: id,
            members_count: selectedRows.length,
            total,
            commission,
            gateway: walletCoversAll ? 'wallet' : selectedCode,
            use_wallet: useWallet,
            wallet_covers_all: walletCoversAll,
        })
        handoff.lock()
        setSubmitting(true)
        try {
            const result = await postGatewayPayment({
                paymentItems: selectedRows.map((row) => ({
                    itemAmount: row.remaining_amount,
                    commission: row.commission,
                    box_id: Number(id),
                    itemId: row.id,
                    itemType: 'msp',
                })),
                // Deliberately absent when the wallet settles the whole
                // payment: naming a bank would make the backend run its
                // per-gateway kill-switch and box allowlist against one it is
                // not going to call.
                ...(needsGateway
                    ? { gateway: { uniqueCode: selectedCode as string } }
                    : {}),
                clientType: 'pwa',
                ...(useWallet ? { use_wallet: true } : {}),
            })

            if (result?.paid_from_wallet) {
                // The backend has already paid every share: no bank to visit,
                // and nothing for the gateway-callback page to finish.
                handoff.release()
                posthog.capture('payment_wallet_paid', {
                    entry: 'cashbox_group_pay',
                    amount: due,
                    items_count: selectedRows.length,
                    box_id: id,
                })
                posthog.capture('cashbox_group_pay_succeeded', {
                    cashbox_id: id,
                    members_count: selectedRows.length,
                    total,
                    paid_from_wallet: true,
                })
                toast.success('پرداخت گروهی از کیف پول انجام شد')
                // The member list, the wallet balance and the payables all moved.
                queryClient.invalidateQueries()
                navigate(`/cashbox/${id}`)
                return
            }

            if (!result?.payment_link) {
                handoff.release()
                posthog.capture('cashbox_group_pay_failed', {
                    cashbox_id: id,
                    members_count: selectedRows.length,
                    status: 'no_payment_link',
                })
                toast.error('لینک پرداخت دریافت نشد')
                return
            }

            // Stored before the redirect: the funds landing reads it back to
            // name the flow once the bank returns.
            rememberPaymentFlow({
                flow_type: 'cashbox_group_pay',
                amount: due,
                gateway: selectedCode,
                box_id: id ?? null,
                box_type: 'csb',
            })
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({ type: 'cashbox_group_pay', data: [] })
            )
            // sendBeacon: the redirect below tears the page down immediately and
            // a normal XHR capture does not survive it.
            posthog.capture(
                'cashbox_group_pay_succeeded',
                {
                    cashbox_id: id,
                    members_count: selectedRows.length,
                    total,
                    has_pay_url: true,
                    use_wallet: useWallet,
                },
                { transport: 'sendBeacon' }
            )
            // The button stays down while the browser is on its way to the bank.
            handoff.hold()
            goToGateway(result.payment_link)
        } catch (error: any) {
            handoff.release()
            posthog.capture('cashbox_group_pay_failed', {
                cashbox_id: id,
                members_count: selectedRows.length,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        } finally {
            setSubmitting(false)
        }
    }

    if (boxLoading || isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت گروهی" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox?.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت گروهی" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند پرداخت گروهی انجام دهد.
                    </Notice>
                </div>
            </div>
        )

    const busy = submitting || handoff.locked

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="پرداخت گروهی" />
            </div>

            <div className="px-4 pt-3">
                <Notice>
                    سهم اعضایی را که هنوز پرداخت نکرده‌اند می‌توانید یکجا
                    بپردازید. مبلغ از حساب شما کسر می‌شود و به سهم آن‌ها ثبت
                    می‌شود.
                </Notice>

                {rows.length === 0 ? (
                    <p className="text-sm text-text-secondary text-center py-10">
                        همه‌ی اعضا سهم این نوبت را پرداخت کرده‌اند
                    </p>
                ) : (
                    <>
                        <div className="mt-3">
                            {rows.map((row) => (
                                <label
                                    key={row.id}
                                    className="flex items-center gap-3 bg-white border border-divider rounded-card p-3 mb-2 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.has(row.id)}
                                        onChange={() =>
                                            setSelected((prev) => {
                                                const next = new Set(prev)
                                                if (next.has(row.id))
                                                    next.delete(row.id)
                                                else next.add(row.id)
                                                return next
                                            })
                                        }
                                        className="w-4 h-4 accent-green-1"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-black truncate">
                                            {row.name?.trim() ||
                                                row.phone_number}
                                        </p>
                                        <p className="text-xs text-text-secondary mt-0.5">
                                            {row.phone_number}
                                        </p>
                                    </div>
                                    <p className="text-sm font-bold text-black shrink-0">
                                        {toman(row.remaining_amount)}
                                    </p>
                                </label>
                            ))}
                        </div>

                        {selectedRows.length > 0 && (
                            <Card className="mt-3">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-sm text-text-secondary">
                                        {faPlain.format(selectedRows.length)} نفر
                                    </span>
                                    <span className="text-sm font-bold text-black">
                                        {toman(total)}
                                    </span>
                                </div>
                                {commission > 0 && (
                                    <div className="flex justify-between items-baseline mt-2">
                                        <span className="text-sm text-text-secondary">
                                            کارمزد
                                        </span>
                                        <span className="text-sm text-black">
                                            {toman(commission)}
                                        </span>
                                    </div>
                                )}
                                {useWallet && (
                                    <div className="flex justify-between items-baseline mt-2">
                                        <span className="text-sm text-text-secondary">
                                            از کیف پول
                                        </span>
                                        <span className="text-sm text-black">
                                            {toman(Math.min(balance, due))}
                                        </span>
                                    </div>
                                )}

                                {hasWallet && (
                                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-divider pt-3">
                                        <div className="min-w-0">
                                            <p className="text-sm text-text-body">
                                                استفاده از کیف پول
                                            </p>
                                            <p className="text-[13px] text-text-secondary">
                                                موجودی {toman(balance)}
                                            </p>
                                        </div>
                                        <Switch
                                            onChange={() =>
                                                setUseWallet(!useWallet)
                                            }
                                            checked={useWallet}
                                            onColor="#75B844"
                                            onHandleColor="#EAEEF0"
                                            handleDiameter={22}
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                            activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                            height={26}
                                            width={50}
                                            className="react-switch shrink-0"
                                            offColor="#B6BCC8"
                                        />
                                    </div>
                                )}

                                {needsGateway && (
                                    <div className="mt-3">
                                        {hasNoGateway ? (
                                            <p className="text-xs text-[#B3261E]">
                                                درگاهی برای پرداخت این صندوق
                                                فعال نیست.
                                            </p>
                                        ) : (
                                            <GatewayPicker
                                                gateways={gateways}
                                                selectedCode={selectedCode}
                                                onSelect={(code) => {
                                                    setSelectedCode(code)
                                                    // The last step before
                                                    // پرداخت; a manager who never
                                                    // gets here dropped out
                                                    // earlier than the payment
                                                    // itself.
                                                    posthog.capture(
                                                        'cashbox_group_pay_gateway_selected',
                                                        {
                                                            cashbox_id: id,
                                                            gateway: code,
                                                        }
                                                    )
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </Card>
                        )}

                        <div className="mt-4">
                            <PrimaryButton
                                disabled={
                                    selectedRows.length === 0 ||
                                    (needsGateway && !selectedCode) ||
                                    busy
                                }
                                onClick={pay}
                            >
                                {busy
                                    ? walletCoversAll
                                        ? 'در حال پرداخت…'
                                        : 'در حال انتقال به درگاه…'
                                    : walletCoversAll
                                    ? `پرداخت ${toman(due)} از کیف پول`
                                    : `پرداخت ${toman(payable)}`}
                            </PrimaryButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
